import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Datatable} from '../../../../components/Datatable/Datatable'
import type {MetaType} from '../../../../components/Datatable/Datatable'
import {Button} from '../../../../components/Button/Button'
import useModals from '../../../../hooks/useModals'
import {DELETE, GET} from '../../../../services/api'
import {Loading} from '../../../../components/Loading/Loading'
import {ModalDelete} from '../../../../components/Modals/General/ModalDelete/ModalDelete'
import {useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import {useSwitchStatus} from '../../../../components/Datatable/SwitchStatus'
// import {intervalValue} from './hooks/dateHour'

type ServiceRequestResponseType = {
  id: number
  title: string
  description: string
  status: boolean
  created: string
  roomServiceForms: any[]
  visibleTo: 'employees' | 'customers' | 'both'
}

const Content: FC = () => {
  const navigate = useNavigate()
  const {showDeleteModal} = useModals()
  const [loading, setLoading] = useState(false)
  const {switchStatus} = useSwitchStatus()
  const [serviceRequests, setServiceRequests] = useState<ServiceRequestResponseType[]>([])
  const [id, setId] = useState<number | null>(null)
  const [, setMeta] = useState<MetaType | undefined>(undefined)

  const getServiceRequests = async () => {
    try {
      setLoading(true)
      const res = await GET(`/room-service`)
      const processedData = res.data.map((row: ServiceRequestResponseType) => {
        const componentsCount = row.roomServiceForms?.length || 0
        const gridsCount =
          row.roomServiceForms?.reduce((total, form) => {
            return total + (form.roomServiceFormGrids?.length || 0)
          }, 0) || 0

        return {
          id: row.id,
          title: row.title,
          description: row.description,
          status: row.status,
          created: row.created,
          visibleTo:
            row.visibleTo === 'employees'
              ? i18n.t('service_requests.visible_to_employees')
              : row.visibleTo === 'customers'
              ? i18n.t('service_requests.visible_to_customers')
              : i18n.t('service_requests.visible_to_both'),
          components: `${componentsCount} ${
            componentsCount !== 1
              ? i18n.t('service_requests.components_plural')
              : i18n.t('service_requests.component')
          }`,
          grids: `${gridsCount} ${
            gridsCount !== 1
              ? i18n.t('service_requests.grids_plural')
              : i18n.t('service_requests.grid')
          }`,
        }
      })
      setServiceRequests(processedData)
      setMeta(res.meta)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  const deleteServiceRequest = async () => {
    try {
      setLoading(true)
      const res = await DELETE(`/room-service/${id}`)
      if (res.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('general.success')),
          text: String(i18n.t('service_requests.success_deleted')),
        }).then(async () => await getServiceRequests())
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('general.error')),
          text: String(i18n.t('service_requests.error_delete')),
        }).then(async () => await getServiceRequests())
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('general.error')),
        text: String(i18n.t('service_requests.error_delete')),
      }).then(async () => await getServiceRequests())
      console.log(e)
    }
  }

  useEffect(() => {
    getServiceRequests()
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('service_requests.title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('service_requests.subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t('service_requests.create_form')}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/structured-content/service-requests/create')}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={serviceRequests}
                headers={[
                  {
                    key: String(i18n.t('service_requests.form_title')),
                    value: 'title',
                    type: 'default',
                  },
                  {
                    key: String(i18n.t('service_requests.form_components')),
                    value: 'components',
                    type: 'default',
                  },
                  {
                    key: String(i18n.t('service_requests.form_grids')),
                    value: 'grids',
                    type: 'default',
                  },
                  {
                    key: String(i18n.t('service_requests.form_status')),
                    value: 'status',
                    type: 'check',
                    action: (item: any) =>
                      switchStatus({endpoint: 'room-service', Id: item.id, status: item.status}),
                  },
                  {
                    key: String(i18n.t('service_requests.visible_to_label')),
                    value: 'visibleTo',
                    type: 'default',
                  },
                  {key: String(i18n.t('service_requests.actions')), value: 'acoes', type: ''},
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (item) => {
                      navigate(`/hospitality/structured-content/service-requests/edit/${item.id}`)
                    },
                    title: String(i18n.t('service_requests.edit_form')),
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => {
                      showDeleteModal(String(i18n.t('service_requests.delete')))
                      setId(item.id)
                    },
                    title: String(i18n.t('service_requests.delete_form')),
                  },
                ]}
                color='primary'
              />
            </div>
          </div>

          <ModalDelete confirmAction={() => deleteServiceRequest()} />
        </div>
      )}
    </>
  )
}

const ServiceRequestsIndex: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: String(i18n.t('service_requests.breadcrumb_dashboard')),
            path: 'dashboard',
            isActive: false,
          },
          {
            title: String(i18n.t('service_requests.breadcrumb_essential_services')),
            path: 'hospitality',
            isActive: false,
          },
          {
            title: i18n.t('service_requests.title') as string,
            path: 'service-requests',
            isActive: true,
          },
        ]}
      >
        {i18n.t('service_requests.title')}
      </PageTitle>
      <Content />
    </>
  )
}

export {ServiceRequestsIndex}
